// Single source of truth for every gauge that can appear in the Orchestrator
// inbox strip. The page renders the user's selection (or role defaults if the
// user hasn't customized) by looking ids up in this registry.
//
// Adding a new gauge: append an entry here, optionally add the id to a role
// default below. The picker and the page automatically pick it up.

import type { ComponentType } from 'react';
import {
  AccessTimeFilled as UrgentIcon,
  InboxOutlined as TriageIcon,
  LightbulbOutlined as OpportunityIcon,
  HowToRegOutlined as ApprovalsIcon,
} from '@mui/icons-material';

import { WI_TYPE_META, WiCategory } from './WiTypeIcon';
import { TriggerType } from './tokens';

export type GaugeFilter =
  | { axis: 'triggers'; values: TriggerType[] }
  | { axis: 'categories'; values: WiCategory[] }
  | { axis: 'taskTypes'; values: string[] };

export interface GaugeDef {
  id: string;
  label: string;
  /** One-line user-facing explanation shown as a tooltip on the picker tile.
   *  The picker labels alone aren't enough for buyers/engineers who don't
   *  know the internal vocabulary (e.g. "what is Vendor Approval?"). */
  description: string;
  color: string;
  Icon: ComponentType<{ sx?: any }>;
  filter: GaugeFilter;
  /** Compute the gauge count from the two count buckets exposed by the
   *  backend /work-items/counts endpoint. */
  getCount: (counts: {
    triggerCounts: Record<string, number>;
    categoryCounts: Record<string, number>;
    taskTypeCounts: Record<string, number>;
  }) => number;
}

const sumTriggers = (keys: TriggerType[], triggerCounts: Record<string, number>) =>
  keys.reduce((acc, t) => acc + (triggerCounts[t] ?? 0), 0);

// ── Cross-cutting / dimension gauges (not 1:1 with a WI category) ──

const TIME_CRITICAL_TRIGGERS: TriggerType[] = ['EOL', 'OBSOLESCENCE', 'RFQ'];
const INTAKE_TRIGGERS: TriggerType[] = ['NEW_BOM', 'PCN', 'MANUAL', 'ERP_CATALOG_REQUEST'];
// Opportunity = anything with value-creation potential: newly-discovered
// alternatives + targeted cost-down + reallocation candidates. The
// "Alternatives" per-category gauge below is a strict subset (CROSSREF_REVIEW
// only). The buyer cares about the broader bucket; the engineer cares about
// the alternatives-review queue specifically — see ROLE_DEFAULT_GAUGES.
const OPPORTUNITY_TRIGGERS: TriggerType[] = ['NEW_ALTERNATIVE', 'COST_DOWN', 'ALLOCATION'];

const dim = (def: GaugeDef): GaugeDef => def;

/**
 * Decisions someone asked for, from any category: a change's approval step, a
 * supplier's deviation request, a vendor approval. Alternative reviews are not
 * here: they are discovered work with their own gauge, and hundreds of them
 * would drown the sign-offs that block someone.
 */
export const APPROVAL_TASK_TYPES = ['CHANGE_APPROVAL', 'SUPPLIER_DEVIATION_REQUEST', 'SUPPLIER_LINK_APPROVAL'];

// ── Per-category gauges (1:1 with the WI category vocabulary) ──

const CAT_DESCRIPTIONS: Record<WiCategory, string> = {
  RFQ:             'RFQs you own — drafts to send, quotes to review, follow-ups due.',
  SUPPLIER_LINK:   'New (part × supplier) relationships waiting for your approval before they\'re added to the catalog.',
  DRAWING_QA:      'Supplier questions about engineering drawings — clarifications, deviations, mfg notes.',
  CROSSREF_REVIEW: 'Alternative parts pending FFF review (form / fit / function approval).',
  PCN:             'Product Change Notices from suppliers — assess impact + decide response.',
  CATALOG_REQUEST: 'Parts someone asked to register as ERP items: complete the details and create the item.',
  CHANGE:          'Change work: approval steps waiting on a decision, and change requests suppliers raised on drawings.',
  PACKAGE:         'Bills whose package verification found items missing required documents: get them, or waive them.',
  OTHER:           'Work items that don\'t fit any specific category.',
};

const cat = (cat: WiCategory): GaugeDef => ({
  id: `cat:${cat}`,
  label: WI_TYPE_META[cat].label,
  description: CAT_DESCRIPTIONS[cat],
  color: WI_TYPE_META[cat].ink,
  Icon: WI_TYPE_META[cat].Icon,
  filter: { axis: 'categories', values: [cat] },
  getCount: ({ categoryCounts }) => categoryCounts[cat] ?? 0,
});

// ── Registry ──

export const GAUGE_REGISTRY: Record<string, GaugeDef> = {
  'time-critical': dim({
    id: 'time-critical',
    label: 'Time-Critical',
    description: 'Items at risk of going past due — overdue RFQs, expiring quotes, EOL/obsolescence with a tight timeline.',
    color: '#dc2626',
    Icon: UrgentIcon,
    filter: { axis: 'triggers', values: TIME_CRITICAL_TRIGGERS },
    getCount: ({ triggerCounts }) => sumTriggers(TIME_CRITICAL_TRIGGERS, triggerCounts),
  }),
  'opportunity': dim({
    id: 'opportunity',
    label: 'Opportunity',
    description: 'Value-creation work: new alternatives discovered, cost-down candidates, reallocation savings. A buyer\'s lens on the alt-review queue.',
    color: '#0f766e',
    Icon: OpportunityIcon,
    filter: { axis: 'triggers', values: OPPORTUNITY_TRIGGERS },
    getCount: ({ triggerCounts }) => sumTriggers(OPPORTUNITY_TRIGGERS, triggerCounts),
  }),
  'intake': dim({
    id: 'intake',
    label: 'Intake',
    description: 'Newly arrived items that haven\'t been triaged yet: new BOMs, fresh PCNs, manual entries, ERP catalog requests.',
    color: '#1d4ed8',
    Icon: TriageIcon,
    filter: { axis: 'triggers', values: INTAKE_TRIGGERS },
    getCount: ({ triggerCounts }) => sumTriggers(INTAKE_TRIGGERS, triggerCounts),
  }),
  'cat:RFQ':             cat('RFQ'),
  'cat:SUPPLIER_LINK':   cat('SUPPLIER_LINK'),
  'cat:DRAWING_QA':      cat('DRAWING_QA'),
  'cat:CROSSREF_REVIEW': cat('CROSSREF_REVIEW'),
  'cat:PCN':             cat('PCN'),
  'cat:CATALOG_REQUEST': cat('CATALOG_REQUEST'),
  'cat:CHANGE':          cat('CHANGE'),
  'cat:PACKAGE':         cat('PACKAGE'),
  'cat:OTHER':           cat('OTHER'),
  approvals: dim({
    id: 'approvals',
    label: 'Approvals',
    description: 'Everything waiting on a decision, from any category: change approval steps, deviation requests, vendor approvals.',
    color: '#9a3412',
    Icon: ApprovalsIcon,
    filter: { axis: 'taskTypes', values: APPROVAL_TASK_TYPES },
    getCount: ({ taskTypeCounts }) => APPROVAL_TASK_TYPES.reduce((acc, t) => acc + (taskTypeCounts[t] ?? 0), 0),
  }),
};

// Stable order for the picker tiles — dimensions first (cross-cutting), then
// categories in workflow order. Other is intentionally last (catch-all).
export const ALL_GAUGE_IDS: string[] = [
  'time-critical',
  'approvals',
  'opportunity',
  'intake',
  'cat:RFQ',
  'cat:SUPPLIER_LINK',
  'cat:DRAWING_QA',
  'cat:CROSSREF_REVIEW',
  'cat:PCN',
  'cat:CATALOG_REQUEST',
  'cat:CHANGE',
  'cat:PACKAGE',
  'cat:OTHER',
];

// ── Role defaults ──
//
// Buyer's daily lens is commercial — they think in terms of opportunities
// (cost savings), RFQs, and vendor approvals. The broader "Opportunity"
// bucket subsumes the per-category Alternatives gauge for buyers, so we
// don't show both (would double-count the same WIs in the strip).
//
// Engineer's lens is technical — drawing Q&A, alternative FFF assessment
// (the per-category Alternatives gauge — same data, different framing),
// PCN technical review. Engineer doesn't think in "opportunity" terms.
export const ROLE_DEFAULT_GAUGES: Record<string, string[]> = {
  buyer: [
    'time-critical',
    'cat:RFQ',
    'cat:SUPPLIER_LINK',
    'opportunity',
    'intake',
  ],
  engineer: [
    'time-critical',
    'approvals',
    'cat:CHANGE',
    'cat:DRAWING_QA',
    'cat:CROSSREF_REVIEW',
    'cat:PCN',
    'intake',
  ],
};

// ── User-selection persistence ──
//
// `null` ⇒ "use role defaults" (initial state, never customized).
// `[...]` ⇒ "use this explicit set" (snapshot of role defaults at first
// customization, then mutated by the picker).

const STORAGE_KEY = (role: string) => `orchestrator.gauges.${role}`;

export const loadUserGaugeSelection = (role: string): string[] | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY(role));
    if (!raw) return null;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return null;
    return arr.filter((x): x is string => typeof x === 'string' && x in GAUGE_REGISTRY);
  } catch {
    return null;
  }
};

export const saveUserGaugeSelection = (role: string, selection: string[]) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY(role), JSON.stringify(selection));
  } catch {
    /* ignore quota / privacy mode */
  }
};

/** Resolve the effective visible gauge ids for the role. User customization
 *  wins if set; otherwise the role defaults. */
export const effectiveGaugeIds = (role: string): string[] => {
  const user = loadUserGaugeSelection(role);
  if (user && user.length > 0) return user;
  return ROLE_DEFAULT_GAUGES[role] ?? ROLE_DEFAULT_GAUGES.buyer;
};
